import React, { useState, useEffect, useCallback } from 'react';
import { FINANCIAL_API, authFetch } from '../constants';
import { useDrillDown, DrillDownRow } from '../components/DrillDown';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';
import { useGlobalEntity } from '../hooks/useGlobalEntity';

const CONSOLIDATED = '__CONSOLIDATED__';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtSigned = (n: number) =>
  n < 0 ? `(${fmt(Math.abs(n))})` : fmt(n);

type ViewMode = 'before' | 'conversion' | 'reporting' | 'adjustments' | 'after' | 'after_all' | 'comparison';
// Phase 13c — ASC 340-40-45-3 split sub-lines carry classification_subline +
// split_source tags. Optional so pre-13c payloads still type-check.
type Account = { account_code: string; account_name: string; balance: number; display_order: number; classification_subline?: 'CURRENT' | 'NON_CURRENT'; split_source?: string };
type Section = { label: string; accounts: Account[]; subtotal: number };

export default function BalanceSheet() {
  const { entity: globalEntity } = useGlobalEntity();
  const [entities, setEntities] = useState<any[]>([]);
  const [entity, setEntity] = useState('');
  const [periods, setPeriods] = useState<string[]>([]);
  const [period, setPeriod] = useState('');
  const [view, setView] = useState<ViewMode>('after');
  const [data, setData] = useState<any>(null);
  const [beforeData, setBeforeData] = useState<any>(null);
  const [afterData, setAfterData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showUSD, setShowUSD] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [pendingInfo, setPendingInfo] = useState<any>(null);

  const drill = useDrillDown();

  const entityCurrency = entities.find(e => e.entity_code === entity)?.functional_currency || '';
  const isUSD = entity === CONSOLIDATED || entityCurrency === 'USD';
  const isConsolidated = entity === CONSOLIDATED;
  const isComparison = view === 'comparison';

  useEffect(() => {
    authFetch(`${FINANCIAL_API}/entities`)
      .then(r => r.json())
      .then(rows => {
        setEntities(rows);
        if (rows.length > 0 && !entity) setEntity(globalEntity || rows[0].entity_code);
      });
  }, []); // eslint-disable-line

  // Sync local entity with global selector when it changes
  useEffect(() => {
    if (globalEntity && globalEntity !== entity) setEntity(globalEntity);
  }, [globalEntity]); // eslint-disable-line

  useEffect(() => {
    if (!entity) return;
    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/available-periods`
      : `${FINANCIAL_API}/available-periods?entity_code=${entity}`;
    authFetch(url)
      .then(r => r.json())
      .then(d => {
        setPeriods(d.periods || []);
        if (d.periods?.length) setPeriod(d.periods[d.periods.length - 1]);
      });
    setShowUSD(false);
  }, [entity, isConsolidated]);

  const buildUrl = useCallback((v: string) => {
    const currSuffix = showUSD && !isUSD ? '&currency=USD' : '';
    return isConsolidated
      ? `${FINANCIAL_API}/consolidated/balance-sheet?period=${period}&view=${v}${currSuffix}`
      : `${FINANCIAL_API}/balance-sheet?entity_code=${entity}&period=${period}&view=${v}${currSuffix}`;
  }, [entity, period, showUSD, isUSD, isConsolidated]);

  const loadData = useCallback(() => {
    if (!entity || !period) return;
    setLoading(true);
    drill.resetDrill();

    if (isComparison) {
      Promise.all([
        authFetch(buildUrl('before')).then(r => r.json()),
        authFetch(buildUrl('after')).then(r => r.json()),
      ]).then(([b, a]) => {
        setBeforeData(b);
        setAfterData(a);
        setData(a);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      authFetch(buildUrl(view))
        .then(r => r.json())
        .then(d => { setData(d); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [entity, period, view, showUSD, isUSD, isConsolidated, isComparison, buildUrl]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!entity) return;
    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/pending-adjustments`
      : `${FINANCIAL_API}/pending-adjustments?entity_code=${entity}`;
    authFetch(url).then(r => r.json()).then(setPendingInfo).catch(() => setPendingInfo(null));
  }, [entity, isConsolidated]);

  const handleExport = async (format: 'pdf' | 'excel') => {
    setExporting(format);
    try {
      const body = {
        statement: 'balance-sheet',
        entity_code: isConsolidated ? 'CONSOLIDATED' : entity,
        period, view,
        currency: showUSD && !isUSD ? 'USD' : undefined,
      };
      const resp = await authFetch(`${FINANCIAL_API}/export/${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `balance-sheet-${entity}-${period}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed:', err); }
    setExporting(null);
  };

  const needsConversion = (() => {
    if (isConsolidated) return true;
    const ent = entities.find(e => e.entity_code === entity);
    return ent && ent.local_framework && ent.local_framework !== ent.reporting_framework;
  })();

  const allViewButtons: { key: ViewMode; label: string }[] = [
    { key: 'before', label: 'Local' },
    { key: 'conversion', label: 'Conversion' },
    { key: 'reporting', label: 'Reporting' },
    { key: 'adjustments', label: 'Adjustments' },
    { key: 'after', label: 'After' },
    { key: 'after_all', label: 'After (All)' },
    { key: 'comparison', label: 'Comparison' },
  ];
  const viewButtons = allViewButtons.filter(v =>
    needsConversion || !['conversion', 'reporting'].includes(v.key)
  );

  const impactColor = (impact: number, sectionType: 'asset' | 'liability' | 'equity') => {
    if (Math.abs(impact) < 0.005) return 'var(--text-hint)';
    const isGood = sectionType === 'asset' ? impact > 0 : impact < 0;
    return isGood ? 'var(--accent-good)' : 'var(--accent-critical)';
  };

  const fmtImpact = (impact: number) =>
    Math.abs(impact) < 0.005 ? '\u2014' : fmtSigned(impact);

  const getAcctBalance = (data: any, sectionKey: string, code: string): number => {
    if (!data?.sections) return 0;
    const section = data.sections[sectionKey];
    if (!section?.accounts) return 0;
    const acct = section.accounts.find((a: Account) => a.account_code === code || a.account_name === code);
    return acct?.balance || 0;
  };

  // === NORMAL VIEW RENDERERS ===
  const renderSection = (section: Section) => (
    <>
      {section.accounts.map(a => (
        <React.Fragment key={a.account_code || a.account_name}>
          <tr
            onClick={() => a.account_code && drill.toggleAccount(a.account_code, entity, period, view, isConsolidated)}
            style={{
              borderBottom: '1px solid var(--border-subtle)',
              cursor: a.account_code ? 'pointer' : 'default',
              background: drill.expandedAcct === a.account_code ? 'var(--accent-subtle)' : 'transparent',
            }}
            onMouseEnter={e => { if (a.account_code && drill.expandedAcct !== a.account_code) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { if (a.account_code && drill.expandedAcct !== a.account_code) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <td style={tdIndent} className="mono">
              {a.account_code && (
                <span style={{ color: 'var(--accent)', marginRight: 6, fontSize: 10 }}>
                  {drill.expandedAcct === a.account_code ? '\u25BC' : '\u25B6'}
                </span>
              )}
              {a.account_code}
            </td>
            <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{a.account_name}</td>
            <td style={tdRight}>{fmtSigned(a.balance)}</td>
          </tr>

          {drill.expandedAcct === a.account_code && (
            <DrillDownRow
              colSpan={3}
              isLoading={drill.acctLoading}
              detail={drill.acctDetail}
              isConsolidated={isConsolidated}
              expandedJE={drill.expandedJE}
              jeDetail={drill.jeDetail}
              jeLoading={drill.jeLoading}
              toggleJE={drill.toggleJE}
            />
          )}
        </React.Fragment>
      ))}
    </>
  );

  const renderSubtotal = (label: string, amount: number, bold = false) => (
    <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: bold ? 'var(--accent-subtle)' : 'transparent' }}>
      <td style={tdStyle}></td>
      <td style={{ ...tdStyle, fontWeight: bold ? 700 : 600, color: 'var(--text-primary)' }}>{label}</td>
      <td style={{
        ...tdRight,
        fontWeight: bold ? 700 : 600,
        borderTop: bold ? '2px solid var(--border-subtle)' : '1px solid var(--text-hint)',
      }}>{fmtSigned(amount)}</td>
    </tr>
  );

  const renderSectionHeader = (label: string) => (
    <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
      <td colSpan={3} style={{
        padding: '10px 14px 4px',
        fontSize: 11, fontWeight: 600,
        color: 'var(--text-hint)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>{label}</td>
    </tr>
  );

  // === COMPARISON RENDERERS ===
  const renderCompSectionHeader = (label: string) => (
    <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
      <td colSpan={5} style={{
        padding: '10px 14px 4px',
        fontSize: 11, fontWeight: 600,
        color: 'var(--text-hint)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>{label}</td>
    </tr>
  );

  const renderCompSection = (sectionKey: string, sType: 'asset' | 'liability' | 'equity') => {
    const afterSection = afterData?.sections?.[sectionKey];
    const beforeSection = beforeData?.sections?.[sectionKey];
    if (!afterSection?.accounts) return null;
    const allAccts = new Map<string, { code: string; name: string }>();
    for (const a of (beforeSection?.accounts || [])) allAccts.set(a.account_code || a.account_name, { code: a.account_code, name: a.account_name });
    for (const a of afterSection.accounts) allAccts.set(a.account_code || a.account_name, { code: a.account_code, name: a.account_name });
    return (
      <>
        {Array.from(allAccts.entries()).map(([key, { code, name }]) => {
          const bBal = getAcctBalance(beforeData, sectionKey, key);
          const aBal = getAcctBalance(afterData, sectionKey, key);
          const impact = aBal - bBal;
          return (
            <tr key={key} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <td style={tdIndent} className="mono">{code}</td>
              <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{name}</td>
              <td style={tdRight}>{fmtSigned(bBal)}</td>
              <td style={tdRight}>{fmtSigned(aBal)}</td>
              <td style={{ ...tdRight, fontWeight: 600, color: impactColor(impact, sType) }}>{fmtImpact(impact)}</td>
            </tr>
          );
        })}
      </>
    );
  };

  const renderCompSubtotal = (label: string, beforeAmt: number, afterAmt: number, sType: 'asset' | 'liability' | 'equity', bold = false) => {
    const impact = afterAmt - beforeAmt;
    return (
      <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: bold ? 'rgba(0,0,0,0.03)' : 'transparent' }}>
        <td style={tdStyle}></td>
        <td style={{ ...tdStyle, fontWeight: bold ? 700 : 600, color: 'var(--text-primary)' }}>{label}</td>
        <td style={{ ...tdRight, fontWeight: bold ? 700 : 600, borderTop: bold ? '2px solid var(--border-subtle)' : '1px solid var(--text-hint)' }}>{fmtSigned(beforeAmt)}</td>
        <td style={{ ...tdRight, fontWeight: bold ? 700 : 600, borderTop: bold ? '2px solid var(--border-subtle)' : '1px solid var(--text-hint)' }}>{fmtSigned(afterAmt)}</td>
        <td style={{ ...tdRight, fontWeight: bold ? 700 : 600, color: impactColor(impact, sType), borderTop: bold ? '2px solid var(--border-subtle)' : '1px solid var(--text-hint)' }}>{fmtImpact(impact)}</td>
      </tr>
    );
  };

  const s = data?.sections;
  const bs = beforeData?.sections;

  return (
    <div style={{ maxWidth: isComparison ? 1100 : 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy-900)', margin: 0 }}>
          Balance Sheet
        </h2>
        {data && (
          <p style={{ fontSize: 13, color: 'var(--text-hint)', margin: '4px 0 0' }}>
            {data.entity_name} &middot; As of {data.period_end} &middot; Currency: {data.currency}
            {isComparison && ' \u00b7 Comparison: Before vs After'}
          </p>
        )}
      </div>

      <FilterBar filters={[
        {
          key: 'entity', label: 'Entity', type: 'select', value: entity,
          onChange: v => setEntity(v),
          options: [
            ...entities.map(e => ({ value: e.entity_code, label: `${e.entity_name} (${e.functional_currency})` })),
            { value: CONSOLIDATED, label: 'Consolidated (USD)' },
          ],
          placeholder: 'Select Entity',
        },
        {
          key: 'period', label: 'Period', type: 'select', value: period,
          onChange: v => setPeriod(v),
          options: periods.map(p => ({ value: p, label: p })),
          placeholder: 'Select Period',
        },
      ]} />

      <TabBar
        tabs={viewButtons.map(v => ({ key: v.key, label: v.label }))}
        active={view}
        onChange={k => setView(k as ViewMode)}
      />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {!isUSD && (
          <ActionButton
            label={showUSD ? 'USD' : entityCurrency}
            onClick={() => setShowUSD(!showUSD)}
            variant={showUSD ? 'primary' : 'secondary'}
            size="sm"
          />
        )}
        <ActionButton label={exporting === 'pdf' ? '...' : 'PDF'} onClick={() => handleExport('pdf')} variant="ghost" size="sm" disabled={!!exporting || !data} />
        <ActionButton label={exporting === 'excel' ? '...' : 'Excel'} onClick={() => handleExport('excel')} variant="ghost" size="sm" disabled={!!exporting || !data} />
        {loading && <span style={{ fontSize: 12, color: 'var(--text-hint)' }}>Loading...</span>}
      </div>

      {/* Provisional warning banner */}
      {view === 'after_all' && (
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>!</span>
          <span style={{ fontSize: 12, color: 'var(--warning)' }}>This view includes <strong>PROVISIONAL</strong> adjustments not yet confirmed by a supervisor.</span>
        </div>
      )}

      {/* Pending adjustments card */}
      {pendingInfo && (pendingInfo.provisional_jes?.count > 0 || pendingInfo.pending_actions?.count > 0 || pendingInfo.unresolved_exceptions?.total > 0) && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', gap: 24, alignItems: 'center', fontSize: 12 }}>
          <span style={{ color: 'var(--text-hint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending</span>
          {pendingInfo.provisional_jes?.count > 0 && (
            <span style={{ color: 'var(--warning)' }}>{pendingInfo.provisional_jes.count} provisional JE{pendingInfo.provisional_jes.count !== 1 ? 's' : ''}</span>
          )}
          {pendingInfo.pending_actions?.count > 0 && (
            <span style={{ color: 'var(--navy-700)' }}>{pendingInfo.pending_actions.count} action{pendingInfo.pending_actions.count !== 1 ? 's' : ''} in review</span>
          )}
          {pendingInfo.unresolved_exceptions?.total > 0 && (
            <span style={{ color: 'var(--text-hint)' }}>{pendingInfo.unresolved_exceptions.total} unresolved exception{pendingInfo.unresolved_exceptions.total !== 1 ? 's' : ''}</span>
          )}
        </div>
      )}

      {/* === COMPARISON VIEW === */}
      {isComparison && s && bs && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 100 }}>Code</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Account</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>Before</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>After</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>Impact</th>
              </tr>
            </thead>
            <tbody>
              {renderCompSectionHeader('Assets')}
              {renderCompSectionHeader(s.current_assets?.label || 'Current Assets')}
              {renderCompSection('current_assets', 'asset')}
              {renderCompSubtotal('Total Current Assets', bs.current_assets?.subtotal || 0, s.current_assets?.subtotal || 0, 'asset')}

              {s.non_current_assets?.accounts?.length > 0 && (
                <>
                  {renderCompSectionHeader(s.non_current_assets.label)}
                  {renderCompSection('non_current_assets', 'asset')}
                  {renderCompSubtotal('Total Non-Current Assets', bs.non_current_assets?.subtotal || 0, s.non_current_assets?.subtotal || 0, 'asset')}
                </>
              )}

              {renderCompSubtotal('TOTAL ASSETS', bs.total_assets || 0, s.total_assets || 0, 'asset', true)}

              <tr><td colSpan={5} style={{ height: 16 }}></td></tr>

              {renderCompSectionHeader('Liabilities')}
              {renderCompSectionHeader(s.current_liabilities?.label || 'Current Liabilities')}
              {renderCompSection('current_liabilities', 'liability')}
              {renderCompSubtotal('Total Current Liabilities', bs.current_liabilities?.subtotal || 0, s.current_liabilities?.subtotal || 0, 'liability')}
              {renderCompSubtotal('TOTAL LIABILITIES', bs.total_liabilities || 0, s.total_liabilities || 0, 'liability', true)}

              <tr><td colSpan={5} style={{ height: 16 }}></td></tr>

              {renderCompSectionHeader("Shareholders' Equity")}
              {renderCompSection('equity', 'equity')}
              {renderCompSubtotal('TOTAL EQUITY', bs.total_equity || 0, s.total_equity || 0, 'equity', true)}

              <tr><td colSpan={5} style={{ height: 8 }}></td></tr>

              {renderCompSubtotal('TOTAL LIABILITIES & EQUITY', bs.total_liabilities_and_equity || 0, s.total_liabilities_and_equity || 0, 'equity', true)}
            </tbody>
          </table>
        </div>
      )}

      {/* === NORMAL VIEW === */}
      {!isComparison && data && s && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 100 }}>Code</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Account</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 160 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {renderSectionHeader('Assets')}
              {renderSectionHeader(s.current_assets.label)}
              {renderSection(s.current_assets)}
              {renderSubtotal('Total Current Assets', s.current_assets.subtotal)}

              {s.non_current_assets.accounts.length > 0 && (
                <>
                  {renderSectionHeader(s.non_current_assets.label)}
                  {renderSection(s.non_current_assets)}
                  {renderSubtotal('Total Non-Current Assets', s.non_current_assets.subtotal)}
                </>
              )}

              {renderSubtotal('TOTAL ASSETS', s.total_assets, true)}

              <tr><td colSpan={3} style={{ height: 16 }}></td></tr>

              {renderSectionHeader('Liabilities')}
              {renderSectionHeader(s.current_liabilities.label)}
              {renderSection(s.current_liabilities)}
              {renderSubtotal('Total Current Liabilities', s.current_liabilities.subtotal)}
              {renderSubtotal('TOTAL LIABILITIES', s.total_liabilities, true)}

              <tr><td colSpan={3} style={{ height: 16 }}></td></tr>

              {renderSectionHeader("Shareholders' Equity")}
              {renderSection(s.equity)}
              {renderSubtotal('TOTAL EQUITY', s.total_equity, true)}

              <tr><td colSpan={3} style={{ height: 8 }}></td></tr>

              {renderSubtotal('TOTAL LIABILITIES & EQUITY', s.total_liabilities_and_equity, true)}
            </tbody>
          </table>
        </div>
      )}

      {!isComparison && data && (
        <div style={{
          marginTop: 16, padding: '10px 16px', borderRadius: 8,
          background: data.is_balanced ? 'var(--positive-bg)' : 'var(--negative-bg)',
          border: `1px solid ${data.is_balanced ? 'var(--accent-good)' : 'var(--accent-critical)'}`,
          fontSize: 13, fontWeight: 600,
          color: data.is_balanced ? 'var(--accent-good)' : 'var(--accent-critical)',
        }}>
          {data.is_balanced
            ? `A = L + E  (${fmt(data.balance_check.total_assets)} = ${fmt(data.balance_check.total_liabilities_and_equity)})`
            : `A \u2260 L + E!  Assets: ${fmt(data.balance_check.total_assets)}, L+E: ${fmt(data.balance_check.total_liabilities_and_equity)}, Diff: ${fmt(data.balance_check.difference)}`
          }
        </div>
      )}

      {isComparison && beforeData && afterData && (() => {
        const totalImpact = (s?.total_assets || 0) - (bs?.total_assets || 0);
        return (
          <div style={{
            marginTop: 16, padding: '10px 16px', borderRadius: 8,
            background: 'var(--accent-subtle)', border: '1px solid var(--accent-border)',
            fontSize: 13, fontWeight: 600, color: 'var(--accent)',
          }}>
            Total Assets Impact: {fmtImpact(totalImpact)} &middot; Net Equity Impact: {fmtImpact((s?.total_equity || 0) - (bs?.total_equity || 0))}
          </div>
        );
      })()}

      {data && !isComparison && !data.sections && !loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 14 }}>
          No data for this entity/period/view combination.
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left',
  fontSize: 11, fontWeight: 600, color: 'var(--text-hint)',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '2px solid var(--border-subtle)',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)',
};

const tdIndent: React.CSSProperties = {
  ...tdStyle, paddingLeft: 28, fontVariantNumeric: 'tabular-nums', fontSize: 12, color: 'var(--text-hint)',
};

const tdRight: React.CSSProperties = {
  ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
};
